import { useState } from 'react';
import { useCreateTrait, useUpdateTrait } from '@/hooks/useMedical';
import type { PersonTrait } from '@/api/medical.api';
import {
  TRAIT_TYPES,
  TRAIT_TYPE_BY_KEY,
  TRAIT_CERTAINTY_OPTIONS,
  TRAIT_CERTAINTY_LABELS,
  TRAIT_OTHER_CODE,
} from './medicalLabels';

interface Props {
  personId: string;
  trait?: PersonTrait;
  onClose: () => void;
}

export function TraitForm({ personId, trait, onClose }: Props) {
  const isEdit = !!trait;
  const createMut = useCreateTrait(personId);
  const updateMut = useUpdateTrait(personId);

  const [traitType, setTraitType] = useState(trait?.traitType ?? TRAIT_TYPES[0].type);
  const def = TRAIT_TYPE_BY_KEY[traitType];

  const [valueCode, setValueCode] = useState(trait?.valueCode ?? '');
  const [valueNumber, setValueNumber] = useState(trait?.valueNumber != null ? String(trait.valueNumber) : '');
  const [valueText, setValueText] = useState(trait?.valueText ?? '');
  const [asOfDateText, setAsOfDateText] = useState(trait?.asOfDateText ?? '');
  const [certainty, setCertainty] = useState<string>(trait?.certainty ?? 'reported');
  const [notes, setNotes] = useState(trait?.notes ?? '');

  const submitting = createMut.isPending || updateMut.isPending;

  // Bei „Sonstiges" (Code-Merkmal mit Freitext) wird value_code='other' um den
  // erfassten Freitext (value_text) ergänzt.
  const isOther = def.kind === 'code' && valueCode === TRAIT_OTHER_CODE;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const base = {
      value_code: def.kind === 'code' ? valueCode || null : null,
      value_number: def.kind === 'number' ? (valueNumber ? Number(valueNumber) : null) : null,
      value_text:
        def.kind === 'text' || isOther ? valueText.trim() || null : null,
      as_of_date_text: asOfDateText.trim() || null,
      certainty,
      notes: notes.trim() || null,
    };
    const opts = { onSuccess: onClose };
    if (isEdit && trait) updateMut.mutate({ id: trait.id, dto: base }, opts);
    else createMut.mutate({ trait_type: traitType, ...base }, opts);
  };

  const hasValue =
    def.kind === 'code'
      ? isOther
        ? !!valueText.trim() // „Sonstiges" erfordert den Freitext
        : !!valueCode
      : def.kind === 'number'
        ? !!valueNumber
        : !!valueText.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">{isEdit ? 'Merkmal bearbeiten' : 'Merkmal hinzufügen'}</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Schließen">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Merkmal</label>
            <select
              value={traitType}
              disabled={isEdit}
              onChange={(e) => { setTraitType(e.target.value); setValueCode(''); setValueNumber(''); setValueText(''); }}
              className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
            >
              {TRAIT_TYPES.map((t) => (
                <option key={t.type} value={t.type}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Wert</label>
            {def.kind === 'code' && (
              <>
                <select
                  value={valueCode}
                  onChange={(e) => { setValueCode(e.target.value); if (e.target.value !== TRAIT_OTHER_CODE) setValueText(''); }}
                  className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <option value="">— bitte wählen —</option>
                  {def.options?.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                  {def.allowFreeText && <option value={TRAIT_OTHER_CODE}>Sonstiges …</option>}
                </select>
                {isOther && (
                  <input
                    value={valueText}
                    onChange={(e) => setValueText(e.target.value)}
                    autoFocus
                    placeholder="Bitte angeben"
                    aria-label="Sonstiger Wert (Freitext)"
                    className="mt-2 w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                )}
              </>
            )}
            {def.kind === 'number' && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={valueNumber}
                  onChange={(e) => setValueNumber(e.target.value)}
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                {def.unit && <span className="text-sm text-slate-500">{def.unit}</span>}
              </div>
            )}
            {def.kind === 'text' && (
              <input
                value={valueText}
                onChange={(e) => setValueText(e.target.value)}
                className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Beleglage</label>
              <select
                value={certainty}
                onChange={(e) => setCertainty(e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                {TRAIT_CERTAINTY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{TRAIT_CERTAINTY_LABELS[c]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Stand</label>
              <input
                value={asOfDateText}
                onChange={(e) => setAsOfDateText(e.target.value)}
                placeholder="z.B. als Kind"
                className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Notizen</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={submitting || !hasValue}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
